import { useEffect, useState } from 'react';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Image from 'next/image';
import LoginModal from './LoginModal';
import { faTimes, faBars, faArrowUp, faArrowDown, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import ScrollProgress from './ScrollProgress';
import { ArrowUp, ArrowDown } from 'lucide-react';

type MainMenuProps = {
  phoneNumber: string | null;
  isLoading: boolean;
  scrollIndicatorEnabled?: boolean;
};

const MainMenu: React.FC<MainMenuProps> = ({ phoneNumber, isLoading, scrollIndicatorEnabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [atTop, setAtTop] = useState(true);

  const handleMenuToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 0);
      setAtTop(window.scrollY === 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    if (atTop) {
      window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <ScrollProgress />
      <LoginModal isOpen={isModalOpen} onClose={handleCloseModal} />
      <header className="fixed top-0 left-0 w-full bg-transparent z-40 shadow-none border-0">
        <nav className="flex items-center justify-between container md:mx-auto shadow-none border-0 backdrop-blur h-24">
          <Link href="/">
            <div>
              <Image
                src="/images/Group1428.png"
                alt="Logo Light Mode"
                className="dark:hidden"
                width={180}
                height={40}
                priority
              />
              <Image
                src="/images/Group1427.png"
                alt="Logo Dark Mode"
                className="hidden dark:block"
                width={180}
                height={40}
                priority
              />
            </div>
          </Link>
          <div className="hidden md:flex items-center space-x-10">
            <Link href="/myeducation">
              <button className="text-career-primary-dark hover:text-purple-500 dark:text-white hover:dark:text-purple-100">
                myEducation
              </button>
            </Link>
            <Link href="/myjob">
              <button className="text-career-primary-dark hover:text-purple-500 dark:text-white hover:dark:text-purple-100">
                myJob
              </button>
            </Link>
            <Link href="/mydesk">
              <button className="text-career-primary-dark hover:text-purple-500 dark:text-white hover:dark:text-purple-100">
                myDesk
              </button>
            </Link>
            <Link href="/mycast">
              <button className="text-career-primary-dark hover:text-purple-500 dark:text-white hover:dark:text-purple-100">
                myCast
              </button>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            {isLoading ? (
              <button
                className="text-sm px-4 py-2 rounded-lg text-transparent bg-purple-500 animate-pulse cursor-not-allowed w-20"
                disabled
              >
                &nbsp;
              </button>
            ) : (
              <>
                {phoneNumber != null ? (
                  <Link href="/profile">
                    <button className="h-[40px] md:px-5 px-3 rounded-lg bg-purple-500 text-white cursor-pointer text-sm">
                      {phoneNumber} <FontAwesomeIcon className="ms-1" icon={faUserCircle} />
                    </button>
                  </Link>
                ) : (
                  <button
                    onClick={handleOpenModal}
                    className="h-[40px] md:px-5 px-3 rounded-lg bg-purple-500 text-white cursor-pointer  text-sm"
                  >
                    Тест бөглөх
                  </button>
                )}
              </>
            )}

            <div className="md:hidden flex items-center z-30">
              <button onClick={handleMenuToggle} aria-label="Menu" className="py-3">
                <FontAwesomeIcon icon={isOpen ? faTimes : faBars} height={20} width={20} />
              </button>
            </div>
          </div>
        </nav>
      </header>

      <div
        className={`md:hidden fixed inset-0 bg-white dark:bg-gray-800 z-30 transition-transform transform ${
          isOpen ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="flex flex-col items-center justify-center h-full">
          <Link href="/myeducation">
            <div className="text-2xl font-bold mb-4 cursor-pointer">myEducation</div>
          </Link>
          <Link href="/myjob">
            <div className="text-2xl font-bold mb-4 cursor-pointer">myJob</div>
          </Link>
          <Link href="/mydesk">
            <div className="text-2xl font-bold mb-4 cursor-pointer z-30">myDesk</div>
          </Link>
          <Link href="/mycast">
            <div className="text-2xl font-bold mb-4 cursor-pointer">myCast</div>
          </Link>
        </div>
      </div>

      {(showScrollTop || atTop) && scrollIndicatorEnabled && (
        <button
          onClick={scrollToTop}
          className="fixed md:bottom-10 md:right-10 md:p-4 bottom-5 right-5 p-2 rounded-full bg-purple-500 text-white shadow-lg hover:bg-purple-600 transition duration-300 z-20"
          aria-label={atTop ? "Scroll down" : "Scroll to top"}
        >
          {atTop ? <ArrowDown size={20} /> : <ArrowUp size={20} />}
        </button>
      )}
    </>
  );
};

export default MainMenu;
